import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { CheckTreePicker } from "rsuite";
import { difference } from "underscore";

import { modulesAtom } from "atoms/GlobalAtoms";
import FieldUtils from "utils/FieldUtils";

/**
 * ReferenceModulePicker - RSUITE Tree picker for module, module subitem reference selection.
 * @param {boolean} includeDynamicModules - Whether or not to include dynamic modules in the tree picker.
 */
const ReferenceModulePicker = ({
  fieldValues,
  setFieldValues,
  includeDynamicModules = false,
}) => {
  const modules = useAtomValue(modulesAtom);

  const TreePickerData = useMemo(() => {
    const data = [];

    for (const module of modules) {
      const moduleSource = `Tenant.Modules.${module.APIName}`;

      if (includeDynamicModules === false && !!module.Dynamic) continue;

      const newItem = {
        label: module.Name,
        value: moduleSource,
      };

      const moduleSubitems = [];

      for (const field of module?.Fields ?? []) {
        if (!field?.FieldType) continue;

        if (FieldUtils.fieldTypeIsSubitem(field.FieldType)) {
          if (field.APIName === "_RemoteSystems") continue;

          moduleSubitems.push({
            label: `${module.Name}'s ${field.Name}`,
            value: `${moduleSource}.${field.APIName}`,
          });
        }
      }

      if (moduleSubitems.length > 0) {
        newItem.children = moduleSubitems;
      }

      data.push(newItem);
    }

    return [{ label: "All", value: "Any" }, ...data];
  }, [modules]);

  const allSources = useMemo(() => {
    let sourceValues = [];
    for (const sourceOption of TreePickerData) {
      if (sourceOption.children) {
        for (const children of sourceOption.children) {
          sourceValues.push(children.value);
        }
      }

      sourceValues.push(sourceOption.value);
    }

    return sourceValues;
  }, [TreePickerData]);

  const sources = useMemo(() => {
    if (!fieldValues.source) return [];

    if (!Array.isArray(fieldValues.source)) {
      return [fieldValues.source];
    } else {
      return fieldValues.source;
    }
  }, [fieldValues.source]);

  return (
    <CheckTreePicker
      data={TreePickerData}
      placeholder={"Select Data Source"}
      placement="bottomEnd"
      defaultExpandAll
      preventOverflow
      onChange={onChange}
      searchable={true}
      block
      value={sources}
      onClean={onClean}
      cascade={false}
    />
  );

  function onClean() {
    setFieldValues((prev) => {
      return {
        ...prev,
        summaryFields: {},
        source: "",
      };
    });
  }

  function onChange(newSources, e) {
    let selectedSources;
    const selectedSource = e.target.value;

    if (newSources.includes("Any") && selectedSource === "String_Any") {
      selectedSources = "Any";
    } else {
      selectedSources = newSources.filter((item) => item !== "Any");
    }

    if (!Array.isArray(selectedSources) && selectedSources === "Any") {
      return setFieldValues((prev) => ({ ...prev, source: selectedSources }));
    }

    // Extract modules names from "Tenant.Modules.ModuleName" format.
    const modNames =
      selectedSources.map((val) => {
        const splitVal = val?.split?.(".");
        return splitVal.length === 3
          ? splitVal[2]
          : `${splitVal[2]}.${splitVal[3]}`;
      }) || [];

    setFieldValues((prev) => {
      const payload = { ...prev, source: selectedSources };
      const hasSummaryFields = prev.summary === true;

      // Handle potential deletion of a summary field
      if (
        hasSummaryFields &&
        Object.keys(prev.summaryFields).length > selectedSources.length
      ) {
        const newSummaryFields = { ...(prev.summaryFields || {}) };
        const diff = difference(Object.keys(prev.summaryFields), modNames);

        delete newSummaryFields[diff[0]];
        payload.summaryFields = newSummaryFields;

        return payload;
      }

      // Add summary fields to summaryFields key if needed
      if (hasSummaryFields) {
        const newSummaryFields = { ...(prev.summaryFields || {}) };

        for (const name of modNames) {
          if (!prev.summaryFields[name]) {
            newSummaryFields[name] = { Fields: [] };
          }
        }

        payload.summaryFields = newSummaryFields;
      }

      return payload;
    });
  }
};

export default ReferenceModulePicker;
